import axios from 'axios'
import errorReporter from '@/plugins/errorReporterPlugin'

const genericError = 'An error has occurred. Your changes likely did not save.'
const loadingError = "We're having trouble loading data. Please try again."

const serviceErrorMessages = {
  name: 'Name is required',
  service_category_id: 'Service Category is required to save the Service or Add-On',
  organization_service_id: 'Organization Service is not in a correct format',
  organization_service_add_on_id: 'Organization Service Add-On is not in a correct format',
}

const getServiceError = (error) => {
  const errorKeys = Object.keys(error?.response?.data || {})
  const errors = errorKeys.map((key) => serviceErrorMessages[key]).filter((message) => message)

  if (errors.length) return errors.join('. ')

  return genericError
}


export const createService = ({ commit, dispatch, state }, service) => {
  axios
    .post('/api/v1/services', service)
    .then(function (response) {
      commit('setShowSaved')
      const newService = response.data.data
      const updatedServices = state.services.concat([newService])
      commit('updateServices', updatedServices)
      scheduleServiceExpansion(commit, newService.id)
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error creating service', error)
    })
}

export const createServiceAddOn = (
  { commit, dispatch, state },
  serviceAddOn
) => {
  axios
    .post('/api/v1/service_add_ons', serviceAddOn)
    .then(function (response) {
      commit('setShowSaved')
      const updatedServiceAddOns = state.serviceAddOns.concat([
        response.data.data,
      ])
      commit('updateServiceAddOns', updatedServiceAddOns)
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error creating service add on', error)
    })
}

export const createServiceModifier = (
  { commit, dispatch, state },
  serviceModifier
) => {
  axios
    .post('/api/v1/service_modifiers', serviceModifier)
    .then(function (response) {
      commit('setShowSaved')
      const updatedServiceModifiers = state.serviceModifiers.concat([
        response.data.data,
      ])
      commit('updateServiceModifiers', updatedServiceModifiers)
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error creating service modifier', error)
    })
}

export const createServiceTax = ({ commit, dispatch, state }, serviceTax) => {
  axios
    .post('/api/v1/service_taxes', serviceTax)
    .then(function (response) {
      commit('setShowSaved')
      const updatedServiceTaxes = state.serviceTaxes.concat([
        response.data.data,
      ])
      commit('updateServiceTaxes', updatedServiceTaxes)
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error creating service tax', error)
    })
}

export const deleteAddOn = ({ commit, dispatch, state }, addOnId) => {
  axios
    .delete('/api/v1/service_add_ons/' + addOnId)
    .then(function (response) {
      commit('setShowSaved')
      commit('removeServiceAddOn', addOnId)
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error deleting addon', error)
    })
}

export const deleteModifier = ({ commit, dispatch, state }, modifierId) => {
  axios
    .delete('/api/v1/service_modifiers/' + modifierId)
    .then(function (response) {
      commit('setShowSaved')
      commit('removeServiceModifier', modifierId)
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error deleting modifier', error)
    })
}

export const deleteTax = ({ commit, dispatch, state }, taxId) => {
  axios
    .delete('/api/v1/service_taxes/' + taxId)
    .then(function (response) {
      commit('setShowSaved')
      commit('removeServiceTax', taxId)
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error deleting tax', error)
    })
}

export const deleteService = ({ commit, dispatch, state }, serviceId) => {
  axios
    .delete('/api/v1/services/' + serviceId)
    .then(function (response) {
      commit('setShowSaved')
      commit('removeService', serviceId)
      dispatch('fetchServices', { deleted: true })
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error deleting service', error)
    })
}

export const duplicateService = ({ commit, dispatch, state }, serviceId) => {
  axios
    .post(`/api/v1/services/${serviceId}/duplicate`)
    .then(function (response) {
      commit('setShowSaved')
      dispatch('fetchServices', { detailed: true })
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error duplicating service', error)
    })
}

export const fetchAgreements = ({ commit, dispatch, state }) => {
  const companyId = state.company.id
  commit('setLoadingObjects', { object: 'Agreements', value: true })
  axios
    .get(`/api/v1/inspection_agreement_templates?company_id=${companyId}`)
    .then(function (response) {
      commit('updateAgreements', response.data.data)
      commit('setLoadingObjects', { object: 'Agreements', value: false })
    })
    .catch(function (error) {
      commit('showError', loadingError)
      commit('setLoadingObjects', { object: 'Agreements', value: false })
      errorReporter.error('Error fetching agreements', error)
    })
}

export const fetchCompany = ({ commit, dispatch, state }, companyId) => {
  return axios
    .get('/api/v1/companies/' + companyId)
    .then((response) => {
      commit('setCompany', response.data.data)
    })
    .catch((error) => {
      commit('showError', loadingError)
      errorReporter.error('Error fetching company', error)
    })
}

export const fetchCustomFields = ({ commit, dispatch, state }) => {
  axios
    .get(`/api/v1/custom_field_templates?company_id=${state.company.id}`)
    .then((response) => {
      commit('setCustomFields', response.data.data)
    })
    .catch((error) => {
      commit('showError', loadingError)
      errorReporter.error('Error fetching custom fields', error)
    })
}

export const fetchServiceCategories = ({ commit, dispatch, state }) => {
  axios.get(`/api/v2/service_categories?company_id=${state.company.id}`).then((response) => {
    commit('setServiceCategories', response.data.data)
  })
}

export const fetchServiceClassifications = ({ commit, dispatch, state }) => {
  axios.get('/api/v1/public/service_classifications').then((response) => {
    commit('setServiceClassifications', response.data.data)
  })
}

export const fetchServices = ({ commit, dispatch, state }, options = {}) => {
  const companyId = state.company.id
  let url = `/api/v1/services?company_id=${companyId}`
  commit('setLoadingObjects', { object: 'Services', value: true })

  if (options.detailed) url += '&detailed=true'
  if (options.deleted) url += '&deleted=true'

  axios
    .get(url)
    .then(function (response) {
      if (!options.deleted) {
        commit('updateServices', response.data.data)
      } else {
        commit('updateUndeletableServices', response.data.data)
      }

      if (options.detailed) handleInclusions(commit, response.data.included)
      commit('setLoadingObjects', { object: 'Services', value: false })
    })
    .catch(function (error) {
      commit('showError', loadingError)
      commit('setLoadingObjects', { object: 'Services', value: false })
      errorReporter.error('Error fetching services', error)
    })
}

export const fetchTemplates = ({ commit, dispatch, state }) => {
  const companyId = state.company.id
  commit('setLoadingObjects', { object: 'Templates', value: true })

  axios
    .get(`/api/v1/templates?company_id=${companyId}`)
    .then(function (response) {
      commit('updateTemplates', response.data.data)
      commit('setLoadingObjects', { object: 'Templates', value: false })
    })
    .catch(function (error) {
      commit('showError', loadingError)
      commit('setLoadingObjects', { object: 'Templates', value: false })
      errorReporter.error('Error fetching templates', error)
    })
}

export const fetchUserProfile = ({ commit, dispatch, state }) => {
  return new Promise((resolve, reject) => {
    axios
      .get('/api/v1/user_profile')
      .then(function (response) {
        commit('setUserProfile', response.data.data)
        resolve(response)
      })
      .catch(function (error) {
        commit('showError', loadingError)
        errorReporter.error('Error fetching user profile', error)
        reject(error)
      })
  })
}

const handleInclusions = (commit, inclusions) => {
  const reducedInclusions = reduceInclusions(inclusions)

  commit('updateServiceAddOns', reducedInclusions.service_add_on || [])
  commit('updateServiceModifiers', reducedInclusions.service_modifier || [])
  commit('updateServiceTaxes', reducedInclusions.service_tax || [])
}

const reduceInclusions = (inclusions) => {
  return inclusions.reduce((summ, value) => {
    if (summ[value.type]) {
      summ[value.type].push(value)
    } else {
      summ[value.type] = [value]
    }

    return summ
  }, {})
}

export const reloadServices = ({ commit, dispatch, state }) => {
  dispatch('fetchServices', { detailed: true })
  dispatch('fetchServices', { deleted: true })
}

const scheduleServiceExpansion = (commit, serviceId) => {
  setTimeout(() => {
    commit('setExpandedServiceId', serviceId)
  }, 100)
}

export const updateService = ({ commit, dispatch, state }, service) => {
  axios
    .put('/api/v1/services/' + service.id, service)
    .then(function (response) {
      commit('setShowSaved')
      commit('updateCollectionRecord', { key: 'services', record: service })
    })
    .catch(function (error) {
      commit('showError', getServiceError(error))
      errorReporter.error('Error updating service', error)
    })
}

export const updateServiceAddOn = ({ commit, dispatch, state }, addOn) => {
  axios
    .put('/api/v1/service_add_ons/' + addOn.id, addOn)
    .then(function (response) {
      commit('setShowSaved')
      commit('updateCollectionRecord', { key: 'serviceAddOns', record: addOn })
    })
    .catch(function (error) {
      commit('showError', getServiceError(error))
      errorReporter.error('Error updating addon', error)
    })
}

export const updateServiceModifier = (
  { commit, dispatch, state },
  modifier
) => {
  axios
    .put('/api/v1/service_modifiers/' + modifier.id, modifier)
    .then(function (response) {
      commit('setShowSaved')
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error updating modifier', error)
    })
}

export const updateServiceTax = ({ commit, dispatch, state }, tax) => {
  axios
    .put('/api/v1/service_taxes/' + tax.id, tax)
    .then(function (response) {
      commit('setShowSaved')
      commit('updateCollectionRecord', { key: 'serviceTaxes', record: tax })
    })
    .catch(function (error) {
      commit('showError', genericError)
      errorReporter.error('Error updating tax', error)
    })
}
